/**
 * Ficha de proceso — plantilla estándar del §2 del brief. Se pinta:
 *   Título · Para qué sirve · Cuándo · Pasos numerados · Actores ·
 *   Documentos · Vídeos · Incidencias.
 */

import { Card, Divider, List, Space, Tag, Typography } from "antd";
import type { Process } from "@/data/onboarding/types";
import { documentsBySlug, videosBySlug } from "@/data/onboarding";
import { DocumentCard } from "./DocumentCard";
import { VideoEmbed } from "./VideoEmbed";

const { Paragraph, Text, Title } = Typography;

interface Props {
  process: Process;
}

export const ProcessCard: React.FC<Props> = ({ process }) => {
  const relatedDocs = (process.documentSlugs ?? [])
    .map((s) => documentsBySlug.get(s))
    .filter((d): d is NonNullable<typeof d> => !!d);
  const relatedVideos = (process.videoSlugs ?? [])
    .map((s) => videosBySlug.get(s))
    .filter((v): v is NonNullable<typeof v> => !!v);

  return (
    <Card
      style={{ marginBottom: 16 }}
      styles={{ body: { display: "flex", flexDirection: "column", gap: 12 } }}
    >
      <div>
        <Title level={4} style={{ marginTop: 0, marginBottom: 4 }}>
          {process.title}
        </Title>
        <Paragraph style={{ marginBottom: 0 }}>{process.purpose}</Paragraph>
      </div>

      <div>
        <Text strong>Cuándo se hace / quién lo encarga:</Text>{" "}
        <Text>{process.when}</Text>
      </div>

      <div>
        <Text strong>Pasos:</Text>
        <ol style={{ marginTop: 4, marginBottom: 0, paddingLeft: 20 }}>
          {process.steps.map((s, i) => (
            <li key={i} style={{ marginBottom: 4 }}>
              {s}
            </li>
          ))}
        </ol>
      </div>

      {process.actors && process.actors.length > 0 ? (
        <div>
          <Text strong>Empresas / actores implicados:</Text>
          <Space size={[6, 6]} wrap style={{ marginTop: 6, display: "flex" }}>
            {process.actors.map((a, i) => (
              <Tag key={i}>{a}</Tag>
            ))}
          </Space>
        </div>
      ) : null}

      {relatedDocs.length > 0 ? (
        <>
          <Divider style={{ margin: "4px 0" }} />
          <Text strong>Documentos relacionados</Text>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
              gap: 12,
            }}
          >
            {relatedDocs.map((d) => (
              <DocumentCard key={d.slug} doc={d} />
            ))}
          </div>
        </>
      ) : null}

      {relatedVideos.length > 0 ? (
        <>
          <Divider style={{ margin: "4px 0" }} />
          <Text strong>Vídeos relacionados</Text>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
              gap: 12,
            }}
          >
            {relatedVideos.map((v) => (
              <VideoEmbed key={v.slug} video={v} />
            ))}
          </div>
        </>
      ) : null}

      {process.incidences && process.incidences.length > 0 ? (
        <>
          <Divider style={{ margin: "4px 0" }} />
          <Text strong>Incidencias frecuentes y cómo actuar</Text>
          <List
            size="small"
            dataSource={process.incidences}
            renderItem={(item) => (
              <List.Item>
                <List.Item.Meta
                  title={item.title}
                  description={item.action}
                />
              </List.Item>
            )}
          />
        </>
      ) : null}
    </Card>
  );
};
